import classnames from 'classnames';
import _ from 'lodash';
import React from 'react';

import { useRouter } from 'next/router';
import Modal from 'react-modal';

import BeautyAwardBadge from 'components/BeautyAwardBadge';
import MonthlyRankingBadge from 'components/MonthlyRankingBadge';
import {
  ChevronLeftSvg,
  CircleArrowSvg,
  CrossSvg,
  MicroscopeSvg,
  RankingSvg,
  TrashSvg,
} from 'components/SorraSvg';
import qs from 'qs';
import Highlighter from 'react-highlight-words';
import useSWR from 'swr';
import { swrGoFetcher } from 'utils/go_api';

import { MixpanelConfig } from 'utils/mixpanelConfig';
import { recordSearchToAnalytics } from 'utils/searchAnalytics';

import api, { swrFetcher } from 'utils/api';
import localStore from 'utils/localStore';
import { getProductOverallRanks } from 'utils/voting2025';

import { useAppLaunch } from 'contexts/AppLaunch';
import { useUserAuth } from 'contexts/UserAuth';

import useRWD from 'hooks/useRWD';

const { useState, useEffect, useCallback, useRef } = React;

const MobileSearchModal = (props) => {
  const {
    show: showSearchModal,
    setShow: setShowSearchModal,
    topSearchDisplayIdx,
  } = props;

  const { isLg } = useRWD();
  const { isSignInStatusReady, user, getAuthorizationHeader } = useUserAuth();

  const router = useRouter();
  const mobileSearchInputRef = useRef(null);

  const { secondCategoryMap, getMonthlyRanks } = useAppLaunch();

  const [localSearchHistory, setLocalSearchHistory] = useState([]);
  useEffect(() => {
    if (isSignInStatusReady && !user) {
      const h = localStore.getSorraSearchHistory();
      setLocalSearchHistory(h);
    }
  }, [isSignInStatusReady, user]);

  const {
    data: searchHistory,
    mutate: mutateSearchHistory,
    isLoading: isSearchHistoryLoading,
  } = useSWR(
    user ? '/api/v3/search/history' : null,
    async (url) => {
      const r = await swrFetcher(url, getAuthorizationHeader);
      return _.filter(r, (h) => {
        return !_.isEmpty(_.trim(h));
      });
    },
    {
      fallbackData: localSearchHistory,
    }
  );

  const { data: topSearches, isLoading: isTopSearchesLoading } = useSWR(
    '/api/v1/search/top',
    async (url) => {
      const data = await swrGoFetcher(url);
      return _.take(data, isLg ? 12 : 8);
    },
    {
      fallbackData: [],
    }
  );

  const [keyword, setKeyword] = useState('');
  const searchQuery = qs.stringify({
    keyword: _.trim(keyword),
  });
  const [instantKeyword, setInstantKeyword] = useState('');
  const debouncedSearchKeyword = useCallback(
    _.debounce((value) => {
      setKeyword(value);
    }, 300),
    [setKeyword]
  );

  useEffect(() => {
    debouncedSearchKeyword(instantKeyword);
  }, [instantKeyword]);
  const { data: recommendations, isLoading: isRecommendationsLoading } = useSWR(
    `/api/v1/search/recommendations?${searchQuery}`,
    swrGoFetcher,
    {
      fallbackData: [],
    }
  );

  const recommendationSecondCategories = _.reduce(
    recommendations,
    (acc, rec) => {
      const { second_categories } = rec;
      return _.uniq([...acc, ...second_categories]);
    },
    []
  );

  const searchPlaceholder = topSearches
    ? topSearches[topSearchDisplayIdx || 0]
    : '';

  const [showClearHistory, setShowClearHistory] = useState(false);

  const routeToSearchPage = (keyword, secondCategoryId) => {
    if (!_.isEmpty(keyword)) {
      const mo = [keyword];
      const q = { keyword };
      if (!_.isNil(secondCategoryId) && secondCategoryMap[secondCategoryId]) {
        const sc = secondCategoryMap[secondCategoryId];
        q.categoryID = sc.id;
        q.category = sc.name;
        mo.unshift(sc.name);
      }
      // setShowSearchModal(false);
      MixpanelConfig.track('search_performed', { search: mo.join(' • ') });
      // 只有這裡算「使用者主動送出的搜尋」，才計入 Algolia
      recordSearchToAnalytics(keyword);
      router.push(`/search?${qs.stringify(q)}`);
      setShowSearchModal(false);
      setInstantKeyword('');
    }
  };

  const handleSearchEnter = () => {
    if (!keyword && searchPlaceholder) {
      routeToSearchPage(searchPlaceholder);
    } else if (!isRecommendationsLoading && recommendations.length) {
      routeToSearchPage(keyword);
    }
  };

  const handleClearSearchHistory = async () => {
    if (user) {
      const headers = await getAuthorizationHeader();
      await api.request({
        method: 'DELETE',
        url: '/api/v3/search/history',
        headers,
      });
      mutateSearchHistory();
      setShowClearHistory(false);
    } else {
      localStore.deleteSorraSearchHistory();
      setLocalSearchHistory([]);
    }
  };

  return (
    <>
      <Modal
        isOpen={showClearHistory}
        shouldCloseOnOverlayClick={false}
        shouldFocusAfterRender={false}
        shouldReturnFocusAfterClose={false}
        className="flex h-full w-full items-center justify-center"
        overlayClassName="z-[65] absolute top-0 left-0 right-0 bottom-0 w-full h-full bg-[#111928] bg-opacity-50"
      >
        <div className="relative rounded-2xl bg-white px-4 py-6">
          <button
            className="absolute right-4 top-4 h-5 w-5"
            onClick={() => {
              setShowClearHistory(false);
            }}
          >
            <CrossSvg />
          </button>
          <p className="px-[80px] text-base font-medium text-[#111928]">
            刪除全部歷史搜索
          </p>
          <div className="mt-9 flex h-12 w-full gap-x-2 text-base font-medium">
            <button
              className="flex flex-1 items-center justify-center rounded-lg border border-sorra-pink bg-white text-sorra-pink"
              onClick={() => {
                setShowClearHistory(false);
              }}
            >
              取消
            </button>
            <button
              className="flex flex-1 items-center justify-center rounded-lg bg-sorra-pink text-white"
              onClick={async () => {
                await handleClearSearchHistory();
                setShowClearHistory(false);
              }}
            >
              確認
            </button>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={showSearchModal}
        shouldCloseOnOverlayClick={false}
        shouldFocusAfterRender={false}
        shouldReturnFocusAfterClose={false}
        className="p-safe-offset block h-full w-full bg-white lg:hidden"
        overlayClassName="z-[60] lg:z-[49] absolute flex items-center justify-center top-0 left-0 right-0 bottom-0 w-full h-full bg-[#111928] bg-opacity-50"
        onAfterOpen={() => {
          mobileSearchInputRef.current.focus();
        }}
      >
        <div className="relative flex h-full w-full flex-col py-6">
          <div className="flex h-12 w-full shrink-0 items-center gap-x-2 px-4">
            <button
              className="h-6 w-6 shrink-0"
              onClick={() => {
                setShowSearchModal(false);
              }}
            >
              <ChevronLeftSvg />
            </button>
            <div className="flex h-full grow items-center gap-x-4 rounded-lg border border-[#D1D5DB] pl-4 pr-2">
              <div className="h-6 w-6">
                <MicroscopeSvg />
              </div>
              <input
                ref={mobileSearchInputRef}
                className="w-full bg-transparent text-base font-medium text-[#111928] placeholder:text-[#9B9B9B] focus:outline-none"
                placeholder={searchPlaceholder}
                value={instantKeyword}
                onChange={(e) => {
                  setInstantKeyword(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.keyCode === 13) {
                    handleSearchEnter();
                  }
                }}
              ></input>
              {keyword && (
                <button
                  className="h-6 w-6"
                  onClick={() => {
                    setInstantKeyword('');
                  }}
                >
                  <CrossSvg color="#9B9B9B" />
                </button>
              )}
            </div>
          </div>
          <div className="no-scrollbar mt-6 min-h-0 w-full grow overflow-hidden">
            <div>
              {keyword && isRecommendationsLoading && (
                <div className="flex w-full items-center justify-center">
                  <img
                    className="h-[145px] w-[145px]"
                    src="/images/search-placeholder.gif"
                  ></img>
                </div>
              )}
              {keyword &&
                !isRecommendationsLoading &&
                recommendations.length === 0 && (
                  <div className="mt-6 flex w-full flex-col items-center justify-center gap-y-3">
                    <img
                      className="h-[73px] w-[73px]"
                      src="/svgs/search-empty.svg"
                    ></img>
                    <div className="font-medium text-sorra-gray-800">
                      沒有搜索結果...
                    </div>
                    <a
                      href="/nominate"
                      className="rounded-lg border border-sorra-pink px-4 py-2 text-sm font-medium text-sorra-pink"
                    >
                      立即提名品牌或產品，即時評價賺分！
                    </a>
                  </div>
                )}
              {!isRecommendationsLoading && recommendations.length > 0 && (
                <div className="pb-8">
                  <div className="flex flex-col gap-y-6 px-4 pb-3 text-sm font-medium text-[#4a4a4a]">
                    {_.take(recommendations, 5).map((recommendation, idx) => {
                      const { id, brand, name, en_name, matchedWords } =
                        recommendation;
                      const display = `${brand ? `${brand} ` : ''} ${name || en_name}`;
                      const overallRank = _.head(getProductOverallRanks(id));
                      const monthlyRank = _.head(getMonthlyRanks(id));

                      return (
                        <div
                          key={id}
                          className="flex cursor-pointer flex-row flex-wrap items-center gap-x-2 gap-y-1"
                          onClick={async () => {
                            const newKeyword = display;
                            if (user) {
                              try {
                                const headers = await getAuthorizationHeader();
                                await api.request({
                                  method: 'post',
                                  url: '/api/v3/search/history',
                                  headers,
                                  data: {
                                    keyword: newKeyword,
                                  },
                                });
                              } catch (_) {
                                // console.error(error);
                              }
                            } else {
                              localStore.addSorraSearchHistory(newKeyword);
                            }
                            window.location.href = `/product/${id}`;
                          }}
                        >
                          <Highlighter
                            highlightClassName="text-sorra-pink bg-transparent"
                            textToHighlight={display}
                            searchWords={matchedWords}
                          />
                          {overallRank && (
                            <BeautyAwardBadge
                              size="s"
                              year={overallRank.year}
                              rank={overallRank.rank}
                              category={overallRank.name}
                            />
                          )}
                          {monthlyRank && (
                            <MonthlyRankingBadge
                              size="s"
                              year={monthlyRank.year}
                              month={monthlyRank.month}
                              rank={monthlyRank.rank}
                              category={monthlyRank.category}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="h-px w-full bg-[#E7E7E7]" />
                  <div className="mt-4 px-4">
                    <p>
                      在分類下搜索關鍵字{' '}
                      <span className="font-medium text-sorra-pink">
                        “{keyword}”
                      </span>
                    </p>
                    <div className="mt-4 grid grid-cols-1 gap-y-6">
                      {_.take(recommendationSecondCategories, 6).map(
                        (secondCategoryId) => {
                          const secondCategoryName = secondCategoryMap[
                            secondCategoryId
                          ]
                            ? secondCategoryMap[secondCategoryId].name
                            : '';
                          return (
                            <div
                              key={secondCategoryId}
                              className="flex h-7 cursor-pointer items-center gap-x-1.5 rounded-full text-sm font-medium text-[#111928]"
                              onClick={() => {
                                routeToSearchPage(keyword, secondCategoryId);
                                // window.location.href = `/search?product=&category=${secondCategoryName}&categoryID=${secondCategoryId}&keyword=${keyword}`;
                              }}
                            >
                              <div className="h-4 w-4">
                                <CircleArrowSvg />
                              </div>
                              <p>{secondCategoryName}</p>
                            </div>
                          );
                        }
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            {!keyword && (
              <div className="px-4">
                {searchHistory.length > 0 && (
                  <div>
                    <div className="flex justify-between text-base">
                      <p className="font-medium text-[#111928]">搜尋記錄</p>
                      <div className="flex gap-x-3">
                        <button
                          className="flex items-center gap-x-1 font-medium text-[#9b9b9b]"
                          onClick={() => {
                            setShowClearHistory(true);
                          }}
                        >
                          <div className="h-5 w-5">
                            <TrashSvg />
                          </div>
                          <p>清除</p>
                        </button>
                      </div>
                    </div>
                    <div className="flex-rwo mt-4 flex flex-wrap gap-x-1.5 gap-y-2">
                      {searchHistory.map((search, idx) => {
                        return (
                          <div
                            key={idx}
                            className="min-w-0 cursor-pointer items-center overflow-hidden truncate rounded-full border border-[#E7E7E7] px-3 py-1 text-xs font-medium text-[#4a4a4a]"
                            onClick={() => {
                              // setShowSearchModal(false);
                              // setSearchPlaceholder('');
                              routeToSearchPage(search);
                            }}
                          >
                            {search}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div
                  className={classnames({
                    'mt-6': searchHistory.length > 0,
                  })}
                >
                  <div className="flex justify-between">
                    <p className="font-medium text-[#111928]">🔥 熱門關鍵字</p>
                    <button
                      className="flex items-center gap-x-1 font-medium text-sorra-pink"
                      onClick={() => {
                        router.push('/ranking');
                        // window.location.href = '/ranking';
                      }}
                    >
                      <div className="h-5 w-5">
                        <RankingSvg />
                      </div>
                      <p>美妝排行榜</p>
                    </button>
                  </div>
                  <div className="mt-5 grid grid-cols-1 gap-y-6">
                    {topSearches.map((search, idx) => {
                      return (
                        <div
                          key={idx}
                          className="flex h-6 cursor-pointer items-center gap-x-1 rounded-full text-sm font-medium text-[#111928]"
                          onClick={() => {
                            routeToSearchPage(search);
                            // router.push(`/search?keyword=${search}`);
                          }}
                        >
                          <div className="h-4 w-4">
                            <CircleArrowSvg />
                          </div>
                          <p>{search}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default MobileSearchModal;
